import { v4 } from 'uuid';
import { AutoFillRule, Company, Employer, FormData } from '../../../types';

type ImportedFormData = Pick<FormData, 'password' | 'companies'>;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeAutoFillRule = (raw: unknown): AutoFillRule => {
  if (!isObject(raw)) {
    throw new Error('Svako "autoFillRules" pravilo mora biti objekt.');
  }
  return {
    id: typeof raw.id === 'string' ? raw.id : v4(),
    startDate: typeof raw.startDate === 'string' ? raw.startDate : '',
    endDate: typeof raw.endDate === 'string' ? raw.endDate : '',
    first: raw.first as AutoFillRule['first'],
    second: raw.second as AutoFillRule['second'],
    vacation: raw.vacation as AutoFillRule['vacation'],
    days: Array.isArray(raw.days) ? raw.days.map((d) => Boolean(d)) : [],
  };
};

const normalizeEmployer = (raw: unknown): Employer => {
  if (!isObject(raw)) {
    throw new Error('Svaki zaposlenik u "employers" mora biti objekt.');
  }
  return {
    id: typeof raw.id === 'string' ? raw.id : v4(),
    name: typeof raw.name === 'string' ? raw.name : '',
    OIB: typeof raw.OIB === 'string' ? raw.OIB : '',
    hours: isObject(raw.hours) ? (raw.hours as Employer['hours']) : {},
    autoFillRules: Array.isArray(raw.autoFillRules)
      ? raw.autoFillRules.map(normalizeAutoFillRule)
      : [],
  };
};

const normalizeCompany = (raw: unknown): Company => {
  if (!isObject(raw)) {
    throw new Error('Svaka firma u "companies" mora biti objekt.');
  }
  return {
    id: typeof raw.id === 'string' ? raw.id : v4(),
    name: typeof raw.name === 'string' ? raw.name : '',
    OIB: typeof raw.OIB === 'string' ? raw.OIB : '',
    address: typeof raw.address === 'string' ? raw.address : '',
    city: typeof raw.city === 'string' ? raw.city : '',
    employers: Array.isArray(raw.employers) ? raw.employers.map(normalizeEmployer) : [],
  };
};

// Parses and normalizes user-provided ERV JSON into the app's FormData shape.
// "year" is intentionally ignored - the form only supports the current year.
// Missing ids (companies/employers/autoFillRules) are generated so hand-written
// JSON doesn't need to include them.
export const parseErvJson = (raw: unknown): ImportedFormData => {
  if (!isObject(raw)) {
    throw new Error('JSON datoteka mora sadržavati objekt.');
  }
  if (!Array.isArray(raw.companies)) {
    throw new Error('JSON datoteka mora sadržavati polje "companies" (popis firmi).');
  }
  return {
    password: typeof raw.password === 'string' ? raw.password : '',
    companies: raw.companies.map(normalizeCompany),
  };
};
